import React, { FC, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { fmtDay, lineOpts } from '../scaling/scalingCard';
import {
  RELEASE_MARKER_PLUGINS,
  ReleaseMarker,
  ReleaseMarkerLegend,
  withReleaseMarkers,
} from '../scaling/releaseMarkers';
import {
  MIN_SAMPLE,
  baselineForGranularity,
  smallSampleStyle,
  xLabels,
} from './series';
import type { BucketLike, Granularity } from './series';

/** Upper bound on x-axis ticks; the last (partial) bucket is always kept. */
const MAX_X_TICKS = 7;

/** Thins ticks to MAX_X_TICKS counting back from the last bucket, which is
 *  always shown and wrapped onto two lines so "(partial)" stays legible. */
function xTick(labels: string[], i: number): string | string[] | null {
  const last = labels.length - 1;
  if (i === last) return labels[i].split(/ (?=\()/);
  const step = Math.ceil(labels.length / MAX_X_TICKS);
  return (last - i) % step === 0 ? labels[i] : null;
}

export const colours = {
  blue: 'rgb(59, 130, 246)',
  green: 'rgb(16, 185, 129)',
  red: 'rgb(220, 38, 38)',
  amber: 'rgb(245, 158, 11)',
  purple: 'rgb(139, 92, 246)',
  slate: 'rgb(100, 116, 139)',
};

export interface Series<T extends BucketLike> {
  label: string;
  value: (b: T) => number | null;
  colour: string;
  /** Pre-ladder baseline for this series, drawn as a flat dashed grey line. */
  baseline?: number | null;
  /** The baseline is a per-week count (rescaled per day on daily charts). */
  baselineIsCount?: boolean;
}

export interface ImpactChartProps<T extends BucketLike> {
  title: string;
  definition: string;
  buckets: readonly T[];
  granularity: Granularity;
  markers: readonly ReleaseMarker[];
  series: Series<T>[];
  /** Observations behind each bucket; points below MIN_SAMPLE render hollow. */
  sampleOf?: (b: T) => number;
  tooltipFooter?: (b: T) => string;
  yTitle?: string;
  /** Percentage axes stop at 100 so a rate can be read at a glance. */
  yMax?: number;
}

const BASELINE_LABEL = 'pre-ladder baseline';

export function ImpactChart<T extends BucketLike>({
  title,
  definition,
  buckets,
  granularity,
  markers,
  series,
  sampleOf,
  tooltipFooter,
  yTitle,
  yMax,
}: ImpactChartProps<T>) {
  const dates = buckets.map((b) => b.bucket);
  const labels = xLabels(buckets, fmtDay);
  const datasets: ChartData<'line'>['datasets'] = series.map((s) => ({
    label: s.label,
    data: buckets.map((b) => s.value(b)),
    borderColor: s.colour,
    backgroundColor: s.colour,
    spanGaps: false,
    ...(sampleOf
      ? smallSampleStyle(buckets, sampleOf, MIN_SAMPLE, s.colour)
      : {}),
  }));
  for (const s of series) {
    const baseline = baselineForGranularity(
      s.baseline,
      s.baselineIsCount === true,
      granularity,
    );
    if (baseline == null) continue;
    const perDay = s.baselineIsCount && granularity === 'daily';
    datasets.push({
      label: `${s.label} — ${BASELINE_LABEL}${perDay ? ' (per day)' : ''}`,
      data: buckets.map(() => baseline),
      borderColor: colours.slate,
      backgroundColor: colours.slate,
      borderDash: [6, 4],
      borderWidth: 1,
      pointRadius: 0,
      pointHitRadius: 0,
    });
  }
  const options = useMemo<ChartOptions<'line'>>(
    () =>
      withReleaseMarkers(
        {
          ...lineOpts,
          scales: {
            ...lineOpts.scales,
            x: {
              ...lineOpts.scales.x,
              ticks: {
                autoSkip: false,
                maxRotation: 0,
                callback: (_value: string | number, i: number) =>
                  xTick(labels, i),
              },
            },
            y: {
              ...lineOpts.scales.y,
              max: yMax,
              title: yTitle ? { display: true, text: yTitle } : undefined,
            },
          },
          plugins: {
            ...lineOpts.plugins,
            legend: { display: series.length > 1, position: 'bottom' },
            tooltip: tooltipFooter
              ? {
                  callbacks: {
                    footer: (items: TooltipItem<'line'>[]) => {
                      const b = buckets[items[0]?.dataIndex ?? -1];
                      return b ? tooltipFooter(b) : '';
                    },
                  },
                }
              : undefined,
          },
        },
        markers,
        dates,
      ),
    [
      buckets,
      dates,
      labels,
      markers,
      series.length,
      tooltipFooter,
      yTitle,
      yMax,
    ],
  );
  return (
    <section className='scaling-card scaling-card--chart'>
      <header className='scaling-card-header'>
        <h3>{title}</h3>
      </header>
      <p className='scaling-card-sub'>{definition}</p>
      <div className='scaling-chart scaling-chart--tall'>
        <Line
          options={options}
          plugins={RELEASE_MARKER_PLUGINS}
          data={{ labels, datasets }}
        />
      </div>
      <ReleaseMarkerLegend markers={markers} labelDates={dates} />
    </section>
  );
}

/** Dashed grey baseline legend note, shown once per section. */
export const BaselineNote: FC<{ label: string }> = ({ label }) => (
  <p className='ladder-baseline-note'>
    Dashed grey lines show the {BASELINE_LABEL}: {label}
  </p>
);
